import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type {
  UnifiedError,
  ErrorFilters,
  ErrorSummary,
  ErrorDistribution,
  EventsPayload,
} from "@/lib/error-types";
import type { Json } from "@/integrations/supabase/types";

// Auth-related event names
const AUTH_EVENT_NAMES = ['AUTH_ERROR', 'BOOTSTRAP_ERROR', 'SESSION_ERROR', 'LOGIN_FAILED'];

interface UseAuthErrorsResult {
  errors: UnifiedError[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  summary: ErrorSummary | null;
  distribution: ErrorDistribution[];
  summaryLoading: boolean;
  distributionLoading: boolean;
  fetchErrors: (filters?: ErrorFilters, page?: number) => Promise<void>;
  fetchSummary: () => Promise<void>;
  fetchDistribution: (startDate?: Date, endDate?: Date) => Promise<void>;
}

// Parse events payload for error details
function parseEventsPayload(payload: Json): EventsPayload {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {};
  }
  return payload as EventsPayload;
}

/**
 * Hook for fetching auth errors from events table
 */
export function useAuthErrors(): UseAuthErrorsResult {
  const [errors, setErrors] = useState<UnifiedError[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<ErrorSummary | null>(null);
  const [distribution, setDistribution] = useState<ErrorDistribution[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [distributionLoading, setDistributionLoading] = useState(false);

  const fetchErrors = useCallback(async (filters?: ErrorFilters, pageNum: number = 1) => {
    setLoading(true);
    try {
      let query = supabase
        .from('events')
        .select('*', { count: 'exact' })
        .in('event_name', AUTH_EVENT_NAMES)
        .order('created_at', { ascending: false });

      // Date filters
      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate.toISOString());
      }
      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate.toISOString());
      }

      // User filter
      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }

      // Pagination
      query = query.range((pageNum - 1) * pageSize, pageNum * pageSize - 1);

      const { data, error, count } = await query;

      if (error) {
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          console.warn('events table not found');
          setErrors([]);
          setTotal(0);
          setPage(pageNum);
          return;
        }
        throw error;
      }

      // Transform to UnifiedError format
      const unifiedErrors: UnifiedError[] = (data || []).map((row) => {
        const payload = parseEventsPayload(row.payload);
        const context = parseEventsPayload(row.context);
        const errorCode = payload.error_code || row.event_name;

        return {
          id: row.id,
          source: 'auth' as const,
          severity: 'error' as const,
          code: errorCode,
          message: payload.message || `${row.event_name} occurred`,
          created_at: row.created_at || row.ts || new Date().toISOString(),
          user_id: row.user_id,
          user_email: null,
          context: {
            session_id: row.session_id || undefined,
            event_name: row.event_name,
          },
          raw_payload: row.payload,
        };
      });

      // Filter by code if specified
      const filtered = filters?.code
        ? unifiedErrors.filter(e => e.code === filters.code)
        : unifiedErrors;

      setErrors(filtered);
      setTotal(count || 0);
      setPage(pageNum);
    } catch (err) {
      console.error('Failed to fetch auth errors:', err);
      toast.error('Failed to load auth errors');
      setErrors([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      // Get auth error counts for 24h
      const { count: count24h } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true })
        .in('event_name', AUTH_EVENT_NAMES)
        .gte('created_at', yesterday.toISOString());

      // Get auth error counts for 7d
      const { count: count7d } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true })
        .in('event_name', AUTH_EVENT_NAMES)
        .gte('created_at', lastWeek.toISOString());

      // Get all auth events for 7d to calculate rate and find most common
      const { data: recentEvents } = await supabase
        .from('events')
        .select('event_name, payload')
        .in('event_name', AUTH_EVENT_NAMES)
        .gte('created_at', lastWeek.toISOString())
        .limit(500);

      // Count error codes
      const codeCounts: Record<string, number> = {};
      for (const row of recentEvents || []) {
        const payload = parseEventsPayload(row.payload);
        const code = payload.error_code || row.event_name;
        codeCounts[code] = (codeCounts[code] || 0) + 1;
      }

      let mostCommonCode: string | null = null;
      let mostCommonCount = 0;
      for (const [code, count] of Object.entries(codeCounts)) {
        if (count > mostCommonCount) {
          mostCommonCode = code;
          mostCommonCount = count;
        }
      }

      setSummary({
        total_24h: count24h || 0,
        total_7d: count7d || 0,
        error_rate_24h: 0, // Auth errors don't have a base rate
        error_rate_7d: 0,
        most_common_code: mostCommonCode,
        most_common_count: mostCommonCount,
      });
    } catch (err) {
      console.error('Failed to fetch auth summary:', err);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchDistribution = useCallback(async (startDate?: Date, endDate?: Date) => {
    setDistributionLoading(true);
    try {
      let query = supabase
        .from('events')
        .select('event_name, payload')
        .in('event_name', AUTH_EVENT_NAMES);

      if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
      }
      if (endDate) {
        query = query.lte('created_at', endDate.toISOString());
      }

      const { data } = await query.limit(500);

      // Count error codes
      const codeCounts: Record<string, number> = {};
      let totalCount = 0;

      for (const row of data || []) {
        const payload = parseEventsPayload(row.payload);
        const code = payload.error_code || row.event_name;
        codeCounts[code] = (codeCounts[code] || 0) + 1;
        totalCount++;
      }

      const dist: ErrorDistribution[] = Object.entries(codeCounts)
        .map(([code, count]) => ({
          code,
          count,
          percentage: totalCount > 0 ? (count / totalCount) * 100 : 0,
        }))
        .sort((a, b) => b.count - a.count);

      setDistribution(dist);
    } catch (err) {
      console.error('Failed to fetch auth distribution:', err);
      setDistribution([]);
    } finally {
      setDistributionLoading(false);
    }
  }, []);

  return {
    errors,
    total,
    page,
    pageSize,
    loading,
    summary,
    distribution,
    summaryLoading,
    distributionLoading,
    fetchErrors,
    fetchSummary,
    fetchDistribution,
  };
}
